import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { orgSettingsApi, type LetterheadSlot } from '../features/orgSettings/api.ts';
import { dv } from '../i18n/dv.ts';
import { type Body } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { ChairDelegationCard } from '../features/orgSettings/ChairDelegationCard.tsx';

function ImageCard({
  slot, label, hint, hasImage, body,
}: { slot: LetterheadSlot; label: string; hint: string; hasImage: boolean; body: Body }): JSX.Element {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (hasImage) {
      void orgSettingsApi.fetchImageBlobUrl(slot, body).then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setPreviewUrl(u);
      }).catch(() => undefined);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [slot, hasImage, body]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org'] });

  const upload = useMutation({
    mutationFn: (file: File) => orgSettingsApi.uploadImage(slot, file, body),
    onSuccess: () => { void invalidate(); },
  });
  const remove = useMutation({
    mutationFn: () => orgSettingsApi.removeImage(slot, body),
    onSuccess: () => { void invalidate(); },
  });

  return (
    <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
      <h2 className="status-label text-muted-foreground">{label}</h2>
      {hasImage && previewUrl && (
        <img src={previewUrl} alt={label} className="max-h-32 rounded-xl border border-[hsl(var(--border))] bg-white object-contain" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="block text-sm"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          className="btn-primary btn-sm"
        >
          <Icon name="upload" size={16} />
          {dv.settings.uploadImage}
        </button>
        {hasImage && (
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="btn-secondary btn-sm"
          >
            {dv.settings.removeImage}
          </button>
        )}
        {upload.isSuccess && <span className="text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
        {upload.isError && <span className="text-sm text-destructive">{dv.common.error}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </section>
  );
}

export function OrgSettings(): JSX.Element {
  const qc = useQueryClient();
  // Council/WDC toggle. The selected body is the query key + the body sent to
  // the body-aware settings GET/PATCH and letterhead calls, so the council tab
  // shows the backfilled council values while the WDC tab is independent.
  const [body, setBody] = useState<Body>('COUNCIL');
  const { data: org, isLoading } = useQuery({
    queryKey: ['org', body],
    queryFn: () => orgSettingsApi.get(body),
  });

  const [quorum, setQuorum] = useState('');
  // Re-sync the local form fields whenever the loaded org (per body) changes so
  // switching Council/WDC shows that body's quorum.
  const syncedRef = useRef<typeof org | null>(null);
  useEffect(() => {
    if (org && syncedRef.current !== org) {
      setQuorum(org.quorum != null ? String(org.quorum) : '');
      syncedRef.current = org;
    }
  }, [org]);

  const saveCadence = useMutation({
    mutationFn: (meetingCadence: 'WEEKLY' | 'MONTHLY' | null) => orgSettingsApi.patch({ body, meetingCadence }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['org'] }); },
  });

  const saveQuorum = useMutation({
    mutationFn: (value: number | null) => orgSettingsApi.patch({ body, quorum: value }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['org'] }); },
  });

  return (
    <div className="space-y-6 lg:space-y-8">
      <BodyToggle body={body} onChange={setBody} />
      {isLoading || !org ? (
        <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
      ) : (
        <>
      <p className="text-sm text-muted-foreground">{dv.settings.letterheadHint}</p>

      <div className="grid gap-6 lg:grid-cols-2">
        <ImageCard key={`header-${body}`} slot="header" label={dv.settings.headerImage} hint={dv.settings.headerSizeHint} hasImage={org.letterhead.hasHeaderImage} body={body} />
        <ImageCard key={`footer-${body}`} slot="footer" label={dv.settings.footerImage} hint={dv.settings.footerSizeHint} hasImage={org.letterhead.hasFooterImage} body={body} />
      </div>

      <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <h2 className="status-label text-muted-foreground">{dv.settings.meetingCadence.title}</h2>
        <p className="text-xs text-muted-foreground">{dv.settings.meetingCadence.help}</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]" role="group">
            {([
              { value: null, label: dv.settings.meetingCadence.notSet },
              { value: 'WEEKLY' as const, label: dv.settings.meetingCadence.weekly },
              { value: 'MONTHLY' as const, label: dv.settings.meetingCadence.monthly },
            ]).map((opt, i) => {
              const active = org.meetingCadence === opt.value;
              return (
                <button
                  key={opt.label}
                  type="button"
                  aria-pressed={active}
                  disabled={saveCadence.isPending}
                  onClick={() => { if (!active) saveCadence.mutate(opt.value); }}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {opt.label}
                </button>
              );
            })}
          </div>
          {saveCadence.isSuccess && <span className="self-center text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
        </div>
      </section>

      <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <h2 className="status-label text-muted-foreground">{dv.settings.quorum.title}</h2>
        <p className="text-xs text-muted-foreground">{dv.settings.quorum.help}</p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = quorum.trim();
            const n = trimmed === '' ? null : Number(trimmed);
            if (n !== null && (!Number.isInteger(n) || n < 1 || n > 100)) return;
            saveQuorum.mutate(n);
          }}
        >
          <input
            type="number"
            min={1}
            max={100}
            dir="ltr"
            placeholder={dv.settings.quorum.placeholder}
            value={quorum}
            onChange={(e) => setQuorum(e.target.value)}
            className="input w-32"
          />
          <button type="submit" disabled={saveQuorum.isPending} className="btn-primary btn-sm">
            {dv.common.save}
          </button>
          <span className="text-sm text-muted-foreground">
            {org.quorum != null ? `#${org.quorum}` : dv.settings.quorum.notSet}
          </span>
          {saveQuorum.isSuccess && <span className="text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
        </form>
      </section>

      <ChairDelegationCard body={body} />
        </>
      )}
    </div>
  );
}
